<template>
  <div>
    <b-row class="mb-5">
      <b-col>
        <t-data-table
          data-test-id="global-domains-data-table"
          with-filter
          :title="$t('Allowed & Blocked Domains')"
          :filter-placeholder="$t('Search Domains')"
          :loading="loading"
          :table-data="dataSet"
          :actions="tableActions"
          @on-delete="onDelete"
          @on-add="onAdd"
          @on-export="onExport"
          @on-row-clicked="onRowClicked"
          @on-import="onImport"
          @export-headers="exportedHeaders"
        />
      </b-col>
    </b-row>
    <section>
      <t-uploader :dialog="modal" @submitFiles="domainSubmit"></t-uploader>
    </section>
  </div>
</template>

<route>
{
  "name": "MSP Global Domains"
}
</route>

<script>
import { mapActions, mapGetters, mapMutations } from "vuex";
import TUploader from "@/components/TUploader";

export default {
  components: {
    TUploader,
  },
  layout: "msp",
  data() {
    return {
      loading: true,
      currentHeaders: null,
      dataSet: {
        fields: [
          { value: "domain", text: this.$t("Domain"), sortable: true },
          { value: "object", text: this.$t("Status"), sortable: true },
          {
            value: "subdomains",
            text: this.$t("Subdomains"),
            sortable: true,
          },
          {
            value: "log_requests",
            text: this.$t("Log Requests"),
            sortable: true,
          },
          {
            value: "comment",
            text: this.$t("Comment"),
            sortable: true,
          },
        ],
        items: [],
      },
      tableActions: {
        add: {
          enabled: true,
          actions: [
            {
              text: this.$t("Allow a Domain"),
              value: {
                name: this.$t("Allow a Domain"),
                modal: "t-add-domain",
                type: "allow",
              },
            },
            {
              text: this.$t("Block a Domain"),
              value: {
                name: this.$t("Block a Domain"),
                modal: "t-add-domain",
                type: "block",
              },
            },
          ],
        },
        import: { enabled: true },
        export: { enabled: true },
        delete: { enabled: true },
      },
    };
  },
  methods: {
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapMutations("modal", ["createModal", "showModal"]),
    ...mapActions("toast", ["displayToast"]),
    ...mapActions("mspDomains", [
      "fetchMSPDomainList",
      "addBlockAllowedMsp",
      "deleteMSPDomainListEntrys",
    ]),
    ...mapActions("fileUpload", [
      "setDialog",
      "clearCache",
      "setDialog",
      "setSpinenr",
    ]),

    async onDelete(domains) {
      try {
        await this.deleteMSPDomainListEntrys({
          id: this.mspId,
          domains,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: domains.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        console.error(err); // eslint-disable-line no-console
      }
    },
    async domainSubmit(value) {
      const action = value.domain.split(" ")[0].toLowerCase();
      const files = value.files;

      try {
        await this.addBlockAllowedMsp({
          policyId: "default",
          action: action,
          file: files,
          msp_id: parseInt(this.mspId),
        });
        this.displayToast({
          title: this.$t("Success"),
          message: "Updated",
          duration: 2000,
          variant: "success",
        });
      } finally {
        this.fetchMSPDomainList({
          id: this.mspId,
          policyId: "default",
        });
        this.setSpinenr(false);
        this.setDialog(false);
      }
    },
    onAdd({ name, modal, type }) {
      this.createModal({
        modal,
        dataTestId: `modal-add-domain-${type}`,
        title: name,
        modalData: {
          isMSP: true,
          mspId: this.mspId,
          type,
        },
        props: {
          size: "lg",
          okTitle: `${
            type === "allow" ? this.$t("Allow") : this.$t("Block")
          } ${this.$t("Domain")}`,
        },
      });

      this.showModal();
    },
    onImport() {
      this.setDialog(true);
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    onExport(items) {
      if (items.length) {
        const exportData = items.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });

          return payload;
        });
        this.$downloadCSV(exportData, "global_domains");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No global domains in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onRowClicked(item) {
      const policy =
        !item.policyname || item.policyname === "Default"
          ? "default"
          : item.policyid;

      const msp_id = this.mspId;

      this.$router.push({
        path: `/msp/global_settings/global_domains/${item.id}?t=${item.object}&p=${policy}&msp_id=${msp_id}`,
      });
    },
  },
  computed: {
    ...mapGetters("mspDomains", ["mspDomainList"]),
    ...mapGetters("globalBlockList", ["globalBlockList"]),
    ...mapGetters("authentication", ["selectedUser", "mspId"]),
    ...mapGetters("fileUpload", ["modal", "getDomain"]),
  },
  watch: {
    mspDomainList() {
      this.dataSet.items = this.mspDomainList;
    },
  },
  async created() {
    this.setPageTitle(this.$t("Global Domains"));
    this.setPageSubtitle([
      this.$t(
        "Block or allow access to particular URLs globally by adding or import the domains here."
      ),
    ]);

    await Promise.all([
      this.fetchMSPDomainList({
        id: this.mspId,
        policyId: "default",
      }),
    ]);

    this.dataSet.items = this.mspDomainList;
    this.loading = false;
  },
};
</script>
